<template>
  <div class="auth-content">
    <div>
      <h1>Log in</h1>
      <p class="auth-switch">
        New to Sonata?
        <router-link :to="{ name: 'create-account', query: $route.query }">
          Create an account
        </router-link>
      </p>
    </div>

    <form
      class="auth-form"
      novalidate
      @submit.prevent="submit"
    >
      <p
        v-for="(err, i) in formErrors.non_field_errors"
        :key="i"
        class="auth-message"
        role="alert"
      >
        {{ err }}
      </p>
      <div class="auth-field">
        <label for="email">Email</label>
        <input
          id="email"
          ref="email"
          v-model.trim="email"
          type="email"
          name="email"
          autocomplete="email"
          placeholder="you@example.com"
          class="auth-input"
          :class="{ 'has-error': formErrors.email }"
          :aria-invalid="formErrors.email ? 'true' : 'false'"
          aria-describedby="email-error"
          :disabled="busy"
          @input="clearFieldError('email')"
        >
        <p
          v-if="formErrors.email"
          id="email-error"
          class="auth-error"
        >
          {{ formErrors.email }}
        </p>
      </div>
      <div class="auth-field">
        <div class="auth-field-head">
          <label for="password">Password</label>
          <a
            href="#"
            @click.prevent="$store.dispatch('auth/showPasswordResetModal')"
          >Forgot password?</a>
        </div>
        <auth-password-input
          ref="password"
          v-model="password"
          autocomplete="current-password"
          :has-error="Boolean(formErrors.password)"
          :disabled="busy"
          @input="clearFieldError('password')"
        />
        <p
          v-if="formErrors.password"
          class="auth-error"
        >
          {{ formErrors.password }}
        </p>
      </div>
      <button
        type="submit"
        class="auth-submit"
        :disabled="busy"
      >
        <span
          v-if="busy"
          class="auth-spin"
        />
        <span>Log in</span>
      </button>
    </form>
  </div>
</template>

<script>
import AuthPasswordInput from '@/components/AuthPasswordInput.vue'
import { defaultDestination, safeNext } from '@/utils/authPages.js'
import { identify as segmentIdentify } from '@/utils/segment.js'
import { identify as logrocketIdentify } from '@/utils/logrocket.js'

export default {
  name: 'LogIn',

  components: {
    AuthPasswordInput
  },

  data () {
    return {
      email: '',
      password: '',
      formErrors: {},
      busy: false
    }
  },

  metaInfo: {
    title: 'Log in'
  },

  computed: {
    destination: function () {
      return safeNext(this.$route.query.next) || defaultDestination()
    }
  },

  mounted () {
    this.$nextTick(() => {
      if (this.$refs.email) this.$refs.email.focus()
    })
  },

  methods: {
    validate () {
      const errors = {}
      if (!this.email) {
        errors.email = 'Enter your email address.'
      }
      if (!this.password) {
        errors.password = 'Enter your password.'
      }
      return errors
    },

    submit () {
      this.formErrors = this.validate()
      if (Object.keys(this.formErrors).length) {
        this.$nextTick(() => {
          this.$refs[this.formErrors.email ? 'email' : 'password'].focus()
        })
        return
      }

      this.busy = true
      return this.$store.dispatch('auth/login', { email: this.email, password: this.password })
        .then(
          () => this.completeLogin(),
          (error) => this.handleLoginError(error)
        )
    },

    completeLogin () {
      // The credentials were accepted, so the user is logged in from here on.
      // If loading their account fails, still send them on rather than report
      // a failed login.
      return this.$store.dispatch('common/initAuthenticated')
        .then(() => this.identify())
        .finally(() => {
          this.busy = false
          this.$router.replace(this.destination)
        })
    },

    handleLoginError (error) {
      this.busy = false
      if (error.response && error.response.status === 401) {
        this.password = ''
        this.formErrors = { non_field_errors: [error.response.data.detail] }
        return
      }
      this.formErrors = { non_field_errors: ['Something went wrong logging in. Please try again.'] }
      return Promise.reject(error.response || error)
    },

    identify () {
      const user = this.$store.getters['user/currentUser']
      const team = this.$store.getters['user/currentTeam']
      const currentSubscription = this.$store.getters['subscriptions/currentSubscription']

      const userTraits = {
        email: user.email,
        name: (user.first_name + ' ' + user.last_name).trim(),
        team: team.name
      }
      if (currentSubscription) {
        Object.assign(userTraits, {
          subscription_status: currentSubscription.status,
          subscription_plan: currentSubscription.plan.product_name,
          subscription_interval: currentSubscription.plan.interval,
          subscription_currency: currentSubscription.plan.currency
        })
      }

      segmentIdentify(user.pk, userTraits)
      logrocketIdentify(user.pk, userTraits)
    },

    clearFieldError (field) {
      if (this.formErrors[field]) {
        this.$delete(this.formErrors, field)
      }
    }
  }
}
</script>
